import { useState } from 'react';
import type { FactionProfileView, ModOrigin } from '../api/catalog';
import { ca } from '../i18n/ca';
import { FactionEmblem } from './FactionEmblem';
import { factionLabel } from './factionNames';
import { ModOriginChip } from './ModOrigin';
import { modOriginLabel } from './modOriginLabel';

const SIDE_CLASS: Record<string, string> = {
  west: 'side-west',
  east: 'side-east',
  resistance: 'side-resistance',
};

/** The combined side filter: every side offered except the ones hidden by default. */
const REST = '*rest';
/** How many recently used factions the row on top remembers. */
const RECENT_MAX = 3;

type Recent = [factionClass: string, side: string];

/** The factions last chosen on one server at one step, newest first; storage may be absent or full. */
function readRecent(key: string | null): Recent[] {
  if (!key) return [];
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(key) ?? '[]');
    if (!Array.isArray(raw)) return [];
    return raw
      .filter(
        (r): r is Recent =>
          Array.isArray(r) && typeof r[0] === 'string' && typeof r[1] === 'string',
      )
      .slice(0, RECENT_MAX);
  } catch {
    return [];
  }
}

function writeRecent(key: string | null, list: Recent[]) {
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(list));
  } catch {
    // Private mode or a full disk: the row is a convenience, not state.
  }
}

/** One card: a faction class, and its profiles on the sides on offer, in the sides' order. */
interface FactionCard {
  key: string;
  factionClass: string;
  label: string;
  picture?: string;
  /**
   * The mod the faction's class comes from, when the catalogue read it: a card
   * says nothing rather than "not read" (the catalogue's faction tab says that).
   */
  origin?: ModOrigin;
  profiles: FactionProfileView[];
}

/** An origin worth a line on a card: a mod, a Creator DLC or the base game, never an unknown. */
function shownOrigin(p: FactionProfileView): ModOrigin | undefined {
  const o = p.origin;
  return o && (o.kind === 'mod' || o.kind === 'cdlc' || o.kind === 'game') ? o : undefined;
}

/**
 * The faction cards of the enemy step and of our own: one picker for both.
 *
 * <p>Two copies of this grid had grown apart and shared the same faults
 * (QUICK_START_REVIEW.md F12): a class profiled on two sides came out as two
 * cards, a mod's class came out as its name, the enemy step offered the
 * players' own side, and forty-five cards had no search. Here a class is one
 * card, with a button per side when it has more than one; a name that is
 * really a class is turned into words and the class goes in the title; a side
 * can be hidden by the step that knows it must not be offered; and the grid
 * scrolls inside its own card rather than growing the page (§2.2).
 */
export function FactionPicker({
  profiles,
  value,
  onChange,
  sides,
  exclude = [],
  onlyWithInfantry = false,
  first = [],
  label,
  refuse,
  onlyAdmitted,
  hideSidesByDefault = [],
  squadFirst = false,
  recent,
}: {
  profiles: FactionProfileView[];
  /** The chosen profile's id. */
  value: number | null;
  onChange: (profile: FactionProfileView) => void;
  /** The sides that may be offered at all, in the order their buttons are drawn. */
  sides: readonly string[];
  /**
   * Sides another step has taken: the players' own on the enemy step, the
   * enemy's on ours. Two factions of one side are allies in the game, so a
   * mission between them is a mission nobody fights. The chosen card is never
   * hidden, so a choice made before the other step cannot vanish unseen.
   */
  exclude?: readonly string[];
  /**
   * Hide the factions the catalogue found no infantry groups for: the enemy
   * step, where a faction of vehicles only would give the night nobody to
   * fight ("Russia (TV)", session gmh56). The chosen card is never hidden.
   */
  onlyWithInfantry?: boolean;
  /**
   * The faction classes that fit the night — its era, and where its terrain is —
   * most fitting first (`/api/setting/defaults`). Their cards lead the grid, in
   * this order and marked, and every other faction is still there to be picked:
   * a proposal, never a filter.
   */
  first?: readonly string[];
  /** The group's accessible name. */
  label: string;
  /**
   * Why a faction cannot be chosen for this night, or null when it can
   * (release D, D5.5: an irregulars-only type). A refused faction is not
   * hidden: its card is folded under «Altres faccions (no admeses)», greyed,
   * with the reason — a whole choice is greyed with its reason, never missing.
   */
  refuse?: (profile: FactionProfileView) => string | null;
  /**
   * Said beside the only faction the night admits, which is then shown fixed
   * («l'única facció irregular d'aquest servidor»).
   */
  onlyAdmitted?: string;
  /**
   * Sides the grid does not show until asked (the enemy step hides BLUFOR: the
   * group is on it). A combined filter of the other sides is the default and
   * the all-sides filter still reaches them; the chosen and the proposed cards
   * stay shown.
   */
  hideSidesByDefault?: readonly string[];
  /** Hand-made factions that play as a squad of their own (the group's) lead the grid. */
  squadFirst?: boolean;
  /**
   * Where the last factions chosen are remembered: the server and the step they
   * were chosen at. Without a server nothing is remembered.
   */
  recent?: { serverId: number | null | undefined; step: string };
}) {
  const [needle, setNeedle] = useState('');
  const [onlySide, setOnlySide] = useState(hideSidesByDefault.length > 0 ? REST : '');
  const t = ca.missionUi.factionPicker;
  const recentKey =
    recent && recent.serverId != null
      ? `ops.newMission.recent.${recent.serverId}.${recent.step}`
      : null;
  const [recents, setRecents] = useState<Recent[]>(() => readRecent(recentKey));
  const pick = (p: FactionProfileView) => {
    if (recentKey) {
      const next = [
        [p.factionClass, p.side] as Recent,
        ...recents.filter(
          ([c, s]) => !(c.toLowerCase() === p.factionClass.toLowerCase() && s === p.side),
        ),
      ].slice(0, RECENT_MAX);
      setRecents(next);
      writeRecent(recentKey, next);
    }
    onChange(p);
  };

  const offered = profiles.filter(
    (p) =>
      sides.includes(p.side) &&
      (p.id === value ||
        (!exclude.includes(p.side) && (!onlyWithInfantry || p.infantry !== false))),
  );
  const withoutInfantry = onlyWithInfantry
    ? profiles.filter(
        (p) =>
          sides.includes(p.side) &&
          !exclude.includes(p.side) &&
          p.infantry === false &&
          p.id !== value,
      ).length
    : 0;
  const byClass = new Map<string, FactionCard>();
  for (const p of offered) {
    // A faction made by hand with a squad of its own (the 1RA, which plays as
    // USMC) is a card of its own: keyed by its class it would have been the
    // same side of the same card as the faction it was made from, and dropped.
    const key = p.squadPresetId ? `squad:${p.id}` : p.factionClass.toLowerCase();
    const card = byClass.get(key);
    if (!card) {
      byClass.set(key, {
        key,
        factionClass: p.factionClass,
        label: factionLabel(p),
        picture: p.picture,
        origin: shownOrigin(p),
        profiles: [p],
      });
    } else if (!card.profiles.some((x) => x.side === p.side)) {
      card.profiles.push(p);
      card.picture = card.picture || p.picture;
      card.origin = card.origin ?? shownOrigin(p);
    }
  }
  const wanted = first.map((f) => f.toLowerCase());
  /** A proposed card's place in the proposal; the rest come after, by name. */
  const rank = (factionClass: string) => {
    const at = wanted.indexOf(factionClass.toLowerCase());
    return at < 0 ? wanted.length : at;
  };
  const isSquad = (c: FactionCard) => squadFirst && c.profiles.some((p) => p.squadPresetId);
  const cards = [...byClass.values()]
    .map((c) => ({
      ...c,
      profiles: [...c.profiles].sort((a, b) => sides.indexOf(a.side) - sides.indexOf(b.side)),
    }))
    .sort(
      (a, b) =>
        Number(isSquad(b)) - Number(isSquad(a)) ||
        rank(a.factionClass) - rank(b.factionClass) ||
        a.label.localeCompare(b.label, 'ca'),
    );
  /** Two cards with one name (two "FIA", two "CDF"): the class tells them apart. */
  const labelCount = new Map<string, number>();
  for (const c of cards) labelCount.set(c.label, (labelCount.get(c.label) ?? 0) + 1);
  const twin = (c: FactionCard) => (labelCount.get(c.label) ?? 0) > 1;

  const q = needle.trim().toLowerCase();
  const present = sides.filter((s) => offered.some((p) => p.side === s));
  const restSides = present.filter((s) => !hideSidesByDefault.includes(s));
  /** The combined filter exists only when it hides something and leaves something. */
  const hasRest = restSides.length > 0 && restSides.length < present.length;
  const restOn = onlySide === REST && hasRest;
  const allOn = onlySide === '' || (onlySide === REST && !hasRest);
  const matching = cards.filter(
    (c) =>
      // A name typed in the search is looked for on every side, hidden or not.
      (allOn ||
        (restOn
          ? q !== '' ||
            c.profiles.some((p) => !hideSidesByDefault.includes(p.side) || p.id === value) ||
            rank(c.factionClass) < wanted.length
          : c.profiles.some((p) => p.side === onlySide))) &&
      // The mod's name too: "rhs" finds every RHS faction whatever it is called.
      (!q ||
        `${c.label} ${c.factionClass} ${c.origin ? modOriginLabel(c.origin) : ''}`
          .toLowerCase()
          .includes(q)),
  );
  // A card is refused when every side of it is: the reason is the first one's.
  const refusalOf = (c: FactionCard) =>
    refuse && c.profiles.every((p) => refuse(p) !== null) ? refuse(c.profiles[0]) : null;
  const shown = matching.filter((c) => refusalOf(c) === null);
  const refused = matching.filter((c) => refusalOf(c) !== null);
  const admittedCount = refuse ? cards.filter((c) => refusalOf(c) === null).length : cards.length;
  const recentProfiles = recents
    .map(([cls, side]) =>
      offered.find((p) => p.factionClass.toLowerCase() === cls.toLowerCase() && p.side === side),
    )
    .filter((p): p is FactionProfileView => p !== undefined && (!refuse || refuse(p) === null));
  const sideName = (side: string) => ca.sideNames[side] ?? side;

  return (
    <div className="faction-picker">
      <div className="filters">
        <input
          type="search"
          name="factionSearch"
          placeholder={t.search}
          aria-label={t.search}
          value={needle}
          onChange={(e) => setNeedle(e.target.value)}
        />
        {present.length > 1 && (
          <span className="row wrap" role="group" aria-label={t.sides}>
            {hasRest && (
              <button
                type="button"
                className={`chip toggle ${restOn ? 'on' : ''}`}
                aria-pressed={restOn}
                onClick={() => setOnlySide(REST)}
              >
                {restSides.map(sideName).join(' + ')}
              </button>
            )}
            <button
              type="button"
              className={`chip toggle ${allOn ? 'on' : ''}`}
              aria-pressed={allOn}
              onClick={() => setOnlySide('')}
            >
              {t.allSides}
            </button>
            {present.map((s) => (
              <button
                key={s}
                type="button"
                className={`chip toggle ${SIDE_CLASS[s] ?? ''} ${onlySide === s ? 'on' : ''}`}
                aria-pressed={onlySide === s}
                onClick={() => setOnlySide(s)}
              >
                {sideName(s)}
              </button>
            ))}
          </span>
        )}
      </div>
      {recentProfiles.length > 0 && (
        <div className="recent-factions row wrap" role="group" aria-label={t.recent}>
          <span className="muted small">{t.recent}</span>
          {recentProfiles.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`chip toggle ${SIDE_CLASS[p.side] ?? ''} ${value === p.id ? 'on' : ''}`}
              aria-pressed={value === p.id}
              title={p.factionClass}
              onClick={() => pick(p)}
            >
              {factionLabel(p)} · {sideName(p.side)}
            </button>
          ))}
        </div>
      )}
      <div className="cards grid pick enemies" role="radiogroup" aria-label={label}>
        {shown.map((c) => {
          const only = c.profiles.length === 1 ? c.profiles[0] : null;
          if (only) {
            return (
              <button
                type="button"
                key={c.key}
                className={`card ${value === only.id ? 'selected' : ''}`}
                role="radio"
                aria-checked={value === only.id}
                title={c.factionClass}
                onClick={() => pick(only)}
              >
                <FactionEmblem
                  name={c.label}
                  factionClass={c.factionClass}
                  side={only.side}
                  picture={c.picture}
                />
                <strong>{c.label}</strong>
                {twin(c) && <span className="muted small">{c.factionClass}</span>}
                <ModOriginChip origin={c.origin} />
                <span className={`chip ${SIDE_CLASS[only.side] ?? ''}`}>{sideName(only.side)}</span>
                {rank(c.factionClass) < wanted.length && (
                  <span className="chip proposed" title={t.proposedHint}>
                    {t.proposed}
                  </span>
                )}
              </button>
            );
          }
          const chosen = c.profiles.some((p) => p.id === value);
          return (
            <div
              key={c.key}
              className={`card multi ${chosen ? 'selected' : ''}`}
              role="group"
              aria-label={c.label}
              title={c.factionClass}
            >
              <FactionEmblem
                name={c.label}
                factionClass={c.factionClass}
                side={c.profiles[0].side}
                picture={c.picture}
              />
              <strong>{c.label}</strong>
              {twin(c) && <span className="muted small">{c.factionClass}</span>}
              <ModOriginChip origin={c.origin} />
              {rank(c.factionClass) < wanted.length && (
                <span className="chip proposed" title={t.proposedHint}>
                  {t.proposed}
                </span>
              )}
              <span className="sides">
                {c.profiles.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    role="radio"
                    aria-checked={value === p.id}
                    aria-label={`${c.label} · ${sideName(p.side)}`}
                    className={`chip toggle ${SIDE_CLASS[p.side] ?? ''} ${value === p.id ? 'on' : ''}`}
                    onClick={() => pick(p)}
                  >
                    {sideName(p.side)}
                  </button>
                ))}
              </span>
            </div>
          );
        })}
      </div>
      {refuse && onlyAdmitted && admittedCount === 1 && shown.length === 1 && (
        <p className="notice small">{onlyAdmitted}</p>
      )}
      {refused.length > 0 && (
        <details className="refused-factions">
          <summary>{t.refused(refused.length)}</summary>
          <p className="muted small">{refusalOf(refused[0])}</p>
          <div className="cards grid pick enemies" aria-label={t.refusedTitle}>
            {refused.map((c) => (
              <button
                type="button"
                key={c.key}
                className="card lacks"
                aria-disabled="true"
                title={refusalOf(c) ?? c.factionClass}
              >
                <FactionEmblem
                  name={c.label}
                  factionClass={c.factionClass}
                  side={c.profiles[0].side}
                  picture={c.picture}
                />
                <strong>{c.label}</strong>
                <span className={`chip ${SIDE_CLASS[c.profiles[0].side] ?? ''}`}>
                  {sideName(c.profiles[0].side)}
                </span>
              </button>
            ))}
          </div>
        </details>
      )}
      {offered.length > 0 && matching.length === 0 && <p className="muted small">{t.none}</p>}
      {withoutInfantry > 0 && <p className="muted small">{t.withoutInfantry(withoutInfantry)}</p>}
    </div>
  );
}
